import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SITE } from '@maktech/shared';
import { api } from '../api/client';
import { LANGS, setLang, type LangCode } from '../i18n';
import { useAuth } from '../features/auth/AuthContext';
import { useCart } from '../features/cart/CartContext';
import { NotificationBell } from './NotificationBell';
import { SearchBox } from './SearchBox';
import { MEGA_MENU, hrefOf } from './navLinks';
import type { CategoryMeta } from '../types/course';

/** The header sits OUTSIDE #view-home, so it stays light on every route. */
function LangToggle() {
  const { i18n } = useTranslation();
  return (
    <div className="lang-toggle" role="group" aria-label="Language" data-testid="lang-toggle">
      {LANGS.map((l) => (
        <button
          key={l.code}
          className={i18n.resolvedLanguage === l.code ? 'on' : ''}
          data-testid={`lang-${l.code}`}
          aria-pressed={i18n.resolvedLanguage === l.code}
          onClick={() => setLang(l.code as LangCode)}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

/**
 * Header — ported from the base navbar (docs/index.html:345) as replaced by
 * the v16 mega-nav (:6010), with v21's MENU_FIX (:6571) applied at source.
 *
 * DELIBERATE FIX — the auth-aware dashboard link.
 * v16 rewrote .nav-links wholesale and its "My Dashboard" is a bare
 * go('dashboard'). The base version (:352) was requireAuth(()=>go('dashboard')),
 * so v16 silently dropped the guard and a guest clicking it lands on a
 * logged-out dashboard. Spec 6.1 names this exactly: "logged out it must read
 * 'Log in / Create account' and open the auth modal. It must never show a
 * 'Welcome back!' dashboard to a guest." The spec is stating a requirement and
 * calling the prototype's behaviour the bug, so the spec wins here.
 */
export function Header({
  onOpenAuth,
  onOpenCart,
}: {
  onOpenAuth: (mode: 'login' | 'signup') => void;
  onOpenCart: () => void;
}) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [openDrop, setOpenDrop] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // "By track" is DERIVED from the live catalog — spec 6.1 is explicit that
  // the category list is "never hardcoded".
  const { data: cats } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<{ categories: CategoryMeta[] }>('/courses/meta/categories')).data,
  });

  // closeNavDrops() (:6041) — click-away closes any open panel.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenDrop(null);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenDrop(null);
    }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  return (
    <nav ref={navRef}>
      <div className="container nav-inner">
        <Link className="logo" to="/courses" data-testid="nav-logo">
          <span className="logo-mark">◆</span>MAKTech Academy
        </Link>

        <div className={`nav-links ${mobileOpen ? 'open' : ''}`} id="navLinks">
          {/* Home is not built (Phase 9); the link lands when the page does. */}

          {MEGA_MENU.map((m) => (
            /**
             * The panel is ALWAYS rendered and visibility is driven by the
             * `on` class on .nav-drop — that is the ported CSS's contract:
             *   .nav-panel{display:none}          (v16.css:9)
             *   .nav-drop.on .nav-panel{display:flex}  (v16.css:12)
             * Conditionally rendering it instead leaves the panel display:none
             * even when mounted, so nothing is clickable.
             */
            <div className={`nav-drop ${openDrop === m.label ? 'on' : ''}`} key={m.label}>
              <button
                className="nav-dt"
                data-testid={`nav-drop-${m.label.toLowerCase()}`}
                aria-expanded={openDrop === m.label}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDrop((d) => (d === m.label ? null : m.label));
                }}
              >
                {m.label}
                <span className="nav-caret">▾</span>
              </button>
              {(
                <div className="nav-panel">
                  {m.cols.map((c) => (
                    <div className="np-col" key={c.h}>
                      <span className="np-h">{c.h}</span>
                      {c.items === 'TRACKS'
                        ? (cats?.categories ?? []).map((t) => (
                            <Link
                              key={t.slug}
                              to={`/courses?category=${t.slug}`}
                              data-testid={`nav-track-${t.slug}`}
                              onClick={() => setOpenDrop(null)}
                            >
                              {t.icon} {t.label ?? t.name}
                            </Link>
                          ))
                        : c.items.map((item) => (
                            <Link
                              key={item.label}
                              to={hrefOf(item.target)}
                              data-testid={`nav-link-${item.label}`}
                              data-pending={item.target.kind !== 'route' ? String(item.target.pendingPhase) : undefined}
                              onClick={() => setOpenDrop(null)}
                            >
                              {item.label}
                            </Link>
                          ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <Link to="/store" data-testid="nav-store">
            {t('nav.store')}
          </Link>

          {/* Staff get a direct link to the admin panel. A student never sees
              it, and the panel + every section endpoint re-check the role
              regardless (spec Part 5). */}
          {user && user.role !== 'student' ? (
            <Link to="/admin" data-testid="nav-admin">
              {t('nav.admin')}
            </Link>
          ) : null}

          {/* Auth-aware — see the note at the top of this file. */}
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard">
              {t('nav.dashboard')}
            </Link>
          ) : (
            <a
              data-testid="nav-dashboard-guest"
              style={{ cursor: 'pointer' }}
              onClick={() => onOpenAuth('login')}
            >
              {t('nav.login_create')}
            </a>
          )}

          <a className="hotline" href={`tel:${SITE.hotline}`} data-testid="nav-hotline">
            ☎ {SITE.hotline}
          </a>

          {/* Mobile-only logout: the nav-cta's Log out button is hidden below
              760px to keep the header from overflowing, so signed-in users log
              out from the hamburger menu instead. */}
          {user ? (
            <a className="mobile-logout" data-testid="nav-logout-mobile" onClick={() => void logout()}>
              {t('nav.logout')}
            </a>
          ) : null}
        </div>

        {/* v18 (:6394) replaces the base nav's Enter-only input entirely. */}
        <SearchBox />

        <div className="nav-cta" id="navCta">
          <button className="cart-btn" data-testid="nav-cart" aria-label="Cart" onClick={onOpenCart}>
            🛒
            {count > 0 ? (
              <span className="cart-count" data-testid="nav-cart-count">
                {count}
              </span>
            ) : null}
          </button>
          <LangToggle />
          {user ? (
            <>
              <NotificationBell />
              <div className="avatar" title={user.name} data-testid="nav-avatar">
                {user.avatarInitials ?? user.name.slice(0, 2).toUpperCase()}
              </div>
              <button className="btn btn-ghost btn-sm" data-testid="nav-logout" onClick={() => void logout()}>
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost btn-sm" data-testid="nav-login" onClick={() => onOpenAuth('login')}>
                {t('nav.login')}
              </button>
              <button className="btn btn-primary btn-sm" data-testid="nav-signup" onClick={() => onOpenAuth('signup')}>
                {t('nav.signup')}
              </button>
            </>
          )}
        </div>

        <button className="hamburger" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
          ☰
        </button>
      </div>
    </nav>
  );
}
